import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Feature, Component, TestCase, Session } from '../../types';
import { apiRequest } from '../../utils/api';
import { formatDate } from '../../utils/date';
import SessionDetails from '../sessions/SessionDetails';
// Theme is applied globally via CSS

const FeatureDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [feature, setFeature] = useState<Feature | null>(null);
  const [components, setComponents] = useState<Component[]>([]);
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [allSessions, setAllSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);

  // Fetch feature when id changes
  useEffect(() => {
    if (!id) return;

    const fetchFeature = async () => {
      try {
        const features = await apiRequest<Feature[]>('/api/features', {}, { useAuth: true });
        const foundFeature = features.find((f: Feature) => f.id === id);
        setFeature(foundFeature || null);
      } catch (error) {
        console.error('Error fetching feature:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeature();
  }, [id]);

  // Fetch components, test cases, and sessions when feature changes
  useEffect(() => {
    if (!feature) return;

    const fetchData = async () => {
      try {
        // Fetch components, test cases, and sessions in parallel
        const [componentsData, testCasesData, sessionsData] = await Promise.all([
          apiRequest<Component[]>(`/api/components?featureId=${feature.id}`, {}, { useAuth: true }).catch(() => []),
          apiRequest<TestCase[]>('/api/testcases', {}, { useAuth: true }).catch(() => []),
          apiRequest<Session[]>(`/api/sessions/by-feature/${feature.id}`, {}, { useAuth: true })
        ]);

        if (Array.isArray(componentsData)) {
          setComponents(componentsData);
        }

        if (Array.isArray(testCasesData)) {
          const componentIds = componentsData.map(c => c.id);
          const filtered = testCasesData.filter((tc: TestCase) => 
            tc.componentId && componentIds.includes(tc.componentId)
          );
          setTestCases(filtered);
        }

        if (Array.isArray(sessionsData)) {
          // Sort by date, most recent first
          sessionsData.sort((a: Session, b: Session) => {
            const getDate = (dateStr: string | undefined) => {
              if (!dateStr) return 0;
              const date = new Date(dateStr);
              return isNaN(date.getTime()) ? 0 : date.getTime();
            };
            const dateA = a.completedAt ? getDate(a.completedAt) : getDate(a.createdAt);
            const dateB = b.completedAt ? getDate(b.completedAt) : getDate(b.createdAt);
            return dateB - dateA;
          });
          setAllSessions(sessionsData);
        }
      } catch (error) {
        console.error('Error fetching feature data:', error);
      }
    };
    fetchData();
  }, [feature]);

  // Get test cases for a component
  const getTestCasesForComponent = (componentId: string): TestCase[] => {
    return testCases.filter(tc => tc.componentId === componentId);
  };

  // Get completed sessions for a specific component
  const getSessionsForComponent = (componentId: string): Session[] => {
    return allSessions.filter(session => {
      // Session must be completed
      if (session.status !== 'completed') return false;
      
      // Session must have this component in its list (check both new and legacy fields)
      const hasComponent = session.components?.includes(componentId) || false;
      return hasComponent;
    });
  };

  // Helper function to check if a component is bugged (includes linked bugs from Additional Bug URLs)
  const isComponentBugged = (session: Session, componentId: string): boolean => {
    // Check if marked as bugged through platform controls (new componentStatuses)
    const componentStatus = session.componentStatuses?.find(status => status.componentId === componentId);
    if (componentStatus?.bugged === true) {
      return true;
    }
    
    // Check if there are linked bugs from Additional Bug URLs
    const linkedBugs = (session.bugUrls || []).filter(bugUrl => {
      if (typeof bugUrl === 'string') return false;
      return bugUrl.componentId === componentId;
    });
    return linkedBugs.length > 0;
  };

  // Get sessions where this component was actually marked as tested
  // Also includes sessions where bugs are linked (even if not explicitly marked as tested)
  const getTestedSessionsForComponent = (componentId: string): Session[] => {
    return getSessionsForComponent(componentId).filter(session => {
      const componentStatus = session.componentStatuses?.find(status => status.componentId === componentId);
      // Consider tested if explicitly marked OR if bugs are linked
      const hasLinkedBugs = (session.bugUrls || []).some(bugUrl => {
        if (typeof bugUrl === 'string') return false;
        return bugUrl.componentId === componentId;
      });
      return componentStatus?.tested === true || hasLinkedBugs;
    });
  };

  // Get success rate for a component
  // Only counts sessions where the component was actually marked as tested
  // 100% = all tested sessions were bug-free
  // 0% = all tested sessions had bugs
  const getComponentSuccessRate = (componentId: string): { rate: number; testedCount: number; totalCount: number } => {
    const allSessions = getSessionsForComponent(componentId);
    const testedSessions = getTestedSessionsForComponent(componentId);
    
    if (testedSessions.length === 0) {
      return { rate: 0, testedCount: 0, totalCount: allSessions.length };
    }
    
    // Count tested sessions where this specific component was bugged
    // Includes both platform-marked bugs and linked bugs from Additional Bug URLs
    const sessionsWithBugs = testedSessions.filter(s => isComponentBugged(s, componentId));
    
    // Success rate = (bug-free tested sessions / total tested sessions) * 100
    const bugFreeSessions = testedSessions.length - sessionsWithBugs.length;
    const rate = (bugFreeSessions / testedSessions.length) * 100;
    
    return { rate, testedCount: testedSessions.length, totalCount: allSessions.length };
  };

  const handleSessionClick = async (sessionId: string) => {
    // Fetch and open session in modal instead of navigating
    try {
      const session = await apiRequest<Session>(`/api/sessions/${sessionId}`, {}, { useAuth: true });
      setSelectedSession(session);
    } catch (error) {
      console.error('Error fetching session:', error);
    }
  };

  if (loading) {
    return <div className="form-container">Loading...</div>;
  }

  if (!feature) {
    return (
      <div className="form-container">
        <div className="card">
          <p>Feature not found.</p>
          <button className="btn btn-secondary" onClick={() => navigate('/features')}>
            Back to Features
          </button>
        </div>
      </div>
    );
  }

  const completedSessions = allSessions.filter(s => s.status === 'completed');

  return (
    <div className="form-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1 className="page-title">{feature.name}</h1>
          {feature.description && (
            <p className="page-subtitle">{feature.description}</p>
          )}
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/features')}>
          Back to Features
        </button>
      </div>

      {completedSessions.length > 0 && (
        <div className="card alert-success mb-md">
          <h3 className="text-success">Feature Overview</h3>
          <div className="flex gap-xl mt-sm">
            <div>
              <span className="text-secondary">Total Completed Sessions:</span> <strong className="value">{completedSessions.length}</strong>
            </div>
            <div>
              <span className="text-secondary">Components:</span> <strong className="text-primary">{components.length}</strong>
            </div>
          </div>
        </div>
      )}

      <div className="mb-md">
        <h2 className="title-lg">Completed Sessions by Component</h2>
        <p className="subtitle mt-sm">
          Each session appears under all components it tested. Click a session to view details.
        </p>
      </div>

      {components.length === 0 ? (
        <div className="card">
          <p>No components found for this feature.</p>
        </div>
      ) : (
        <div>
          {components.map(component => {
            const sessionsForComponent = getSessionsForComponent(component.id);
            const { rate: successRate, testedCount, totalCount } = getComponentSuccessRate(component.id);
            const componentTestCases = getTestCasesForComponent(component.id);
            
            return (
              <div key={component.id} className="card mb-lg">
                <div className="flex justify-between items-center mb-md">
                  <div className="flex-1">
                    <h3 className="title-md">{component.name}</h3>
                    {component.description && (
                      <p className="subtitle mt-sm">{component.description}</p>
                    )}
                    {/* Test Cases as Reference */}
                    {componentTestCases.length > 0 && (
                      <div className="test-cases-reference mt-sm p-sm" style={{ background: 'var(--surface-elevated)', borderRadius: '4px', marginTop: '0.5rem' }}>
                        <div className="label text-xs mb-xs">Test Cases (Reference):</div>
                        <div className="test-cases-list">
                          {componentTestCases.map(testCase => (
                            <div key={testCase.id} className="test-case-item text-sm mb-xs">
                              <span className="text-secondary">• {testCase.title}</span>
                              {testCase.qaseId && (
                                <a
                                  href={testCase.qaseId}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary ml-sm"
                                >
                                  [Qase]
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {sessionsForComponent.length > 0 && (
                    <div className="text-right">
                      <div className="label">Success Rate</div>
                      {testedCount > 0 ? (
                        <>
                          <div className={`stat-value ${successRate >= 80 ? 'success' : successRate >= 50 ? 'warning' : 'danger'}`}>
                            {successRate.toFixed(1)}%
                          </div>
                          <div className="subtitle mt-sm">
                            {testedCount} tested{testedCount !== totalCount && ` / ${totalCount} total`}
                          </div>
                          <div className="hint mt-sm">Based on tested sessions only</div>
                        </>
                      ) : (
                        <>
                          <div className="stat-value text-muted">N/A</div>
                          <div className="subtitle mt-sm">
                            {totalCount} session{totalCount !== 1 ? 's' : ''} (none tested)
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {sessionsForComponent.length === 0 ? (
                  <p className="hint p-md">No completed sessions for this component.</p>
                ) : (
                  <div className="session-grid session-grid-auto">
                    {sessionsForComponent.map(session => {
                      const componentStatus = session.componentStatuses?.find(s => s.componentId === component.id);
                      const wasBugged = isComponentBugged(session, component.id);
                      
                      return (
                        <div
                          key={`${session.id}-${component.id}`}
                          className={`card session-card-clickable ${wasBugged ? 'session-card-bugged' : ''}`}
                          onClick={() => handleSessionClick(session.id)}
                        >
                          <div className="flex justify-between items-start gap-sm">
                            <div className="flex-1 min-w-0">
                              <div className="label mono">{session.id}</div>
                              <div className="value">{session.sessionType}</div>
                              <div className="subtitle mt-sm">
                                {session.user} | {session.platforms && session.platforms.length > 0 ? session.platforms.join(', ') : (session.platform || 'N/A')}
                              </div>
                            </div>
                            <span className="status-badge status-completed">COMPLETED</span>
                          </div>
                          
                          {session.completedAt && (
                            <div className="subtitle mt-sm">
                              Completed: {formatDate(session.completedAt, 'MMM dd, yyyy h:mm a')}
                            </div>
                          )}
                          
                          <div className="mt-sm subtitle">
                            <div>
                              <strong>Component Status:</strong>{' '}
                              {componentStatus?.tested ? (
                                wasBugged ? (
                                  <span className="text-danger">🐛 Bugged</span>
                                ) : (
                                  <span className="text-success">✓ Tested</span>
                                )
                              ) : (
                                <span className="text-muted">Not tested</span>
                              )}
                            </div>
                          </div>
                          
                          <div className="mt-md text-secondary text-sm">
                            Click to view details
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Session Details Modal */}
      {selectedSession && (
        <SessionDetails
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
          zIndex={2000}
        />
      )}
    </div>
  );
};

export default FeatureDetail;
